import React from 'react';

/**
 * Map to store dynamic refs for form fields.
 */
const refMap = new Map<string, React.RefObject<any>>();

/**
 * Define and store a dynamic ref for a form field.
 */
const defineRef = <T>(key: string): React.RefObject<T> => {
  const ref = React.createRef<T>();
  refMap.set(key, ref);
  return ref;
};

/**
 * Store and manage dynamic refs for <RenderComponent /> fields.
 *
 * These are rendered by iterating over config objects like `cardFields`.
 *   - NOTE: `cardFields` corresponds to `formFieldMap.cardFields`
 *
 * @TODO Remove readRef and iterateRef. They don't seem to be used anywhere.
 *
 * @returns object with:
 *   - defineRef: a method to define dynamic refs
 *   - refMap: a map of all defined refs.
 */
export const useDynamicFormFieldRefs = <T>(): {
  /**
   * Store arbitrary refs to <RenderComponent /> fields.
   * - Each directly references the rendered component instance.
   *
   * Name format: `<section>-<fieldKey>` -  e.g. `cardField-cardNumber`.
   *
   * Defined inline, directly in the `ref` prop of <RenderComponent />
   *
   * ### Example:
   * ```tsx
   * <RenderComponent
   *   key={`key-cardFields-${el}`}
   *   ref={defineRef(`cardField-${el}`)}
   *   // ...etc...
   * />
   *```
   */
  defineRef: typeof defineRef<T>;
  /**
   * Map of all defined refs.
   * Used to iterate over all refs when validating & submitting the form.
   *
   * ### Example:
   * ```tsx
   * refMap.forEach((componentRef: any) => {
   *   const validateFunc = componentRef.current?.validate;
   *   if (validateFunc) responses.push(validateFunc());
   * });
   * ```
   */
  refMap: typeof refMap;
} => ({
  defineRef,
  refMap,
});
